import { type ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/cn';

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-fv-muted">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </div>
  );
}

// `bare` drops the box (border/bg) for use inside an existing Panel/card so it
// doesn't render a box-in-box.
export function EmptyState({ title = 'Nothing here yet', hint, bare = false }: { title?: string; hint?: string; bare?: boolean }) {
  return (
    <div
      className={cn(
        'grid place-items-center py-14 text-center',
        !bare && 'rounded-panel border border-dashed border-fv-border bg-white',
      )}
    >
      <Inbox size={28} className="text-fv-muted/60" />
      <p className="mt-3 font-medium text-fv-text">{title}</p>
      {hint && <p className="mt-1 text-sm text-fv-muted">{hint}</p>}
    </div>
  );
}

export function ErrorState({ message, onRetry, bare = false }: { message: string; onRetry?: () => void; bare?: boolean }) {
  return (
    <div
      className={cn(
        'grid place-items-center py-14 text-center',
        !bare && 'rounded-panel border border-fv-error/30 bg-fv-error/5',
      )}
    >
      <AlertTriangle size={26} className="text-fv-error" />
      <p className="mt-3 max-w-md px-4 font-medium text-fv-error">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-3 rounded-lg bg-fv-primary px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-fv-primaryDark"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-panel border border-fv-border bg-white p-5${className ? ` ${className}` : ''}`}>{children}</div>;
}
